import React, { useState, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { btnP, crd } from "../../constants/styles";
import { gradCardStyle, gradLift } from "../../constants/gradCard";
import Fld from "../common/Fld";
import StatusTracker from "../common/StatusTracker";

// ── Compact 3-stage status tracker ──────────────────────────────────
// Pending → Assigned → Resolved. The borrowed UX cue from the reference
// design is the at-a-glance progress visual — far more informative than
// a single status chip. Implemented in-line in the card so users see it
// without an extra interaction.
// StatusTracker moved to src/components/common/StatusTracker.js so
// the landlord MaintPage can render the same 5-step pipeline view.
// Same visual treatment + same state mapping — single source of truth.

function TenMaintList() {
    const { t, h, mts, setMts, tens, pays, go, flash, currUserId, showConfirm } = useContext(Ctx);
    const ct = currUserId;
    const mm = mts.filter(x => x.tid === ct);
    const uc = { high: h.rd, med: h.wn, low: h.gn };

    const [editId, sEd] = useState(null);
    const [eDesc, sED] = useState("");
    const [eUrg, sEU] = useState("");
    // Resolved cards collapse to a single "✓ Resolved" pill; tap expands
    // to reveal the full tracker + timestamps. Keeps the list scannable
    // when there are many historical items.
    const [expandedDoneIds, setExpandedDoneIds] = useState(new Set());
    const toggleExpand = (id) => {
        setExpandedDoneIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const fmtTs = (v) => {
        if (!v) return null;
        const d = new Date(v);
        if (isNaN(d.getTime())) return String(v);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    };

    const startEdit = (m) => { sEd(m.id); sED(m.desc); sEU(m.urg); };
    const saveEdit = () => {
        const updated = mts.map(x => x.id === editId ? { ...x, desc: eDesc, urg: eUrg } : x);
        setMts(updated);
        const updatedItem = updated.find(x => x.id === editId);
        if (updatedItem) api.saveMaintenance(updatedItem);
        flash(t.updated); sEd(null);
    };
    const deleteMaint = (m) => {
        showConfirm(t.deleteMaintConfirm, () => {
            api.deleteMaintenance(m.id);
            setMts(mts.filter(x => x.id !== m.id));
            flash(t.deleted);
        });
    };

    const sorted = [...mm].sort((a, b) => (b.dt || b.id || "").localeCompare(a.dt || a.id || ""));
    const active = sorted.filter(m => m.st !== "done");
    const past = sorted.filter(m => m.st === "done");

    return <div style={{ paddingTop: 16 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: "-0.3px" }}>{t.maintenance}</h2>
            <button onClick={() => go("subMaint")} style={{
                background: h.ac, border: "none", color: "#fff",
                padding: "8px 14px", borderRadius: 10, cursor: "pointer",
                fontSize: 12, fontWeight: 600,
                boxShadow: `0 2px 8px ${h.ac}30`,
            }}>+ {t.add}</button>
        </div>

        {/* Empty state */}
        {sorted.length === 0 && (
            <div style={{ ...crd(h), padding: 32, textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🛠️</div>
                <div style={{ color: h.tx, fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                    {t.noMaintReqs || "No maintenance requests yet"}
                </div>
                <div style={{ color: h.sc, fontSize: 12 }}>
                    {t.tapAddToRaise || "Tap + Add to raise a new request"}
                </div>
            </div>
        )}

        {/* Active section heading */}
        {active.length > 0 && (
            <div style={{
                color: h.sc, fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                letterSpacing: 0.4, marginBottom: 8, marginTop: 4,
            }}>
                {t.currentRequests || "Current Requests"} · {active.length}
            </div>
        )}

        {sorted.map(m => {
            if (editId === m.id) {
                return (
                    <div key={m.id} style={{ ...crd(h), padding: 20 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                            <div style={{ color: h.ac, fontWeight: 600, fontSize: 14 }}>{t.editRequest}</div>
                            <button onClick={() => sEd(null)} style={{ background: "none", border: "none", color: h.sc, cursor: "pointer" }}>✕</button>
                        </div>
                        <Fld l={t.desc} v={eDesc} set={sED} h={h} />
                        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.urgency}</label>
                        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                            {["low", "med", "high"].map(u => (
                                <button key={u} onClick={() => sEU(u)} style={{
                                    flex: 1, padding: 8, borderRadius: 10,
                                    border: `1px solid ${eUrg === u ? h.ac : h.bd}`,
                                    background: eUrg === u ? `${h.ac}15` : "transparent",
                                    color: eUrg === u ? h.ac : h.sc,
                                    cursor: "pointer", fontSize: 12,
                                }}>
                                    {u === "low" ? t.low : u === "med" ? t.med : t.high}
                                </button>
                            ))}
                        </div>
                        <button onClick={saveEdit} style={btnP(h)}>{t.save}</button>
                    </div>
                );
            }

            // Sub-status label inside "Assigned" stage
            const subStatus = m.sp_status === "accepted"     ? (t.accepted || "Accepted")
                            : m.sp_status === "in_progress" ? (t.inProgress || "In Progress")
                            : m.sp_status === "completed"   ? (t.completed || "Completed")
                            : null;

            // Section divider before the first "done" item
            const isFirstDone = m.st === "done" && past[0]?.id === m.id;
            const isDone = m.st === "done";
            const isExpanded = expandedDoneIds.has(m.id);

            // Collapsed view for resolved items — tap to expand.
            if (isDone && !isExpanded) {
                return (
                    <React.Fragment key={m.id}>
                        {isFirstDone && active.length > 0 && (
                            <div style={{
                                color: h.sc, fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                                letterSpacing: 0.4, marginBottom: 8, marginTop: 16,
                            }}>
                                {t.history || "History"} · {past.length}
                            </div>
                        )}
                        <div onClick={() => toggleExpand(m.id)}
                            onMouseOver={(e) => gradLift(e, "green", true)}
                            onMouseOut={(e) => gradLift(e, "green", false)}
                            style={{
                            ...crd(h),
                            ...gradCardStyle("green"),
                            padding: "12px 14px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                        }}>
                            <div style={{
                                width: 28, height: 28, borderRadius: "50%",
                                background: "rgba(255,255,255,0.22)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                            }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    color: "#fff", fontWeight: 600, fontSize: 13,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}>
                                    {m.desc}
                                </div>
                                <div style={{ color: "#fff", fontSize: 11, fontWeight: 600, marginTop: 2 }}>
                                    {(t.resolved || "Resolved")}
                                    {(m.confirmed_at || m.dt) && (
                                        <span style={{ color: "rgba(255,255,255,0.8)", fontWeight: 400 }}> · {fmtTs(m.confirmed_at) || m.dt}</span>
                                    )}
                                </div>
                            </div>
                            <span style={{ color: "#fff", fontSize: 16, lineHeight: 1 }}>▾</span>
                        </div>
                    </React.Fragment>
                );
            }

            return (
                <React.Fragment key={m.id}>
                    {isFirstDone && active.length > 0 && (
                        <div style={{
                            color: h.sc, fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                            letterSpacing: 0.4, marginBottom: 8, marginTop: 16,
                        }}>
                            {t.history || "History"} · {past.length}
                        </div>
                    )}

                    <div
                        // Resolved (done) cards toggle back to the
                        // collapsed pill when tapped anywhere on
                        // non-interactive area. We use a closest()
                        // check on the event target rather than
                        // requiring every inner button to call
                        // stopPropagation manually — that pattern is
                        // brittle (one missed button = accidental
                        // collapse on tap). closest() matches buttons,
                        // links, inputs, textareas, selects, labels.
                        onClick={isDone
                          ? (e) => {
                              if (e.target.closest('button, a, input, textarea, select, label')) return;
                              toggleExpand(m.id);
                            }
                          : undefined}
                        style={{
                            ...crd(h),
                            padding: 16,
                            opacity: m.st === "done" ? 0.85 : 1,
                            cursor: isDone ? "pointer" : "default",
                        }}>
                        {/* Top row: ticket id (subtle) + urgency chip */}
                        <div style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            marginBottom: 8,
                        }}>
                            <div style={{
                                color: h.sc, fontSize: 10, fontWeight: 600,
                                letterSpacing: 0.4, textTransform: "uppercase",
                            }}>
                                #{(m.id || "").replace(/^m/, "M-").slice(0, 8)}
                            </div>
                            <span style={{
                                display: "inline-flex", alignItems: "center",
                                background: `${uc[m.urg]}18`,
                                color: uc[m.urg],
                                padding: "2px 8px",
                                borderRadius: 999,
                                fontSize: 10, fontWeight: 700,
                                letterSpacing: 0.3,
                                textTransform: "uppercase",
                            }}>
                                {m.urg === "high" ? t.high : m.urg === "med" ? t.med : t.low}
                            </span>
                        </div>

                        {/* Title + date */}
                        {/* Title row; shows an up-chevron when this is
                            a done card (visual affordance that clicking
                            collapses it — matches the down-chevron on
                            the collapsed pill above). */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 2 }}>
                            <div style={{ color: h.tx, fontWeight: 700, fontSize: 15, letterSpacing: "-0.1px", flex: 1, minWidth: 0 }}>
                                {m.desc}
                            </div>
                            {isDone && (
                                <span style={{ color: h.sc, fontSize: 14, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">▴</span>
                            )}
                        </div>
                        <div style={{ color: h.sc, fontSize: 11 }}>
                            {m.dt || "—"}
                            {subStatus && <span style={{ marginLeft: 8, color: h.ac, fontWeight: 600 }}>· {subStatus}</span>}
                        </div>

                        {/* Status tracker */}
                        <StatusTracker st={m.st} sp_status={m.sp_status} h={h} />

                        {/* Assigned info */}
                        {m.st === "assigned" && m.asn_n && (
                            <div style={{
                                background: `${h.wn}0d`,
                                borderRadius: 10,
                                padding: 10,
                                marginTop: 14,
                                border: `1px solid ${h.wn}30`,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}>
                                <div>
                                    <div style={{ color: h.wn, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>
                                        {t.assignedTo}
                                    </div>
                                    <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>🛠️ {m.asn_n}</div>
                                </div>
                                {m.asn_p && (
                                    <a href={`tel:${m.asn_p}`} style={{
                                        background: h.gn || "#16a34a", color: "#fff",
                                        padding: "6px 12px", borderRadius: 999,
                                        textDecoration: "none", fontSize: 11, fontWeight: 600,
                                    }}>📞 Call</a>
                                )}
                            </div>
                        )}

                        {/* SP completion confirmation */}
                        {m.completion_token && !m.confirmed_by_tenant && m.sp_status === "completed" && (
                            <button onClick={async () => {
                                const res = await api.serviceVerifyCompletion(m.completion_token, ct);
                                if (res.success) {
                                    setMts(mts.map(x => x.id === m.id ? { ...x, confirmed_by_tenant: true, confirmed_at: new Date().toISOString(), st: "done" } : x));
                                    flash(t.completionConfirmed);
                                }
                            }} style={{
                                width: "100%", padding: 12, borderRadius: 10, border: "none",
                                background: h.gn || "#16a34a", color: "#fff",
                                fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 12,
                            }}>{"✓ " + t.confirmCompletion}</button>
                        )}

                        {m.confirmed_by_tenant && m.st !== "done" && (
                            <div style={{
                                background: `${h.gn || "#16a34a"}10`, borderRadius: 8,
                                padding: 8, marginTop: 12, textAlign: "center",
                            }}>
                                <span style={{ color: h.gn || "#16a34a", fontSize: 12, fontWeight: 600 }}>
                                    {"✓ " + t.completionVerified}
                                </span>
                            </div>
                        )}

                        {/* Resolved details */}
                        {m.st === "done" && (
                            <div style={{
                                background: `${h.gn || "#16a34a"}0d`,
                                borderRadius: 10, padding: 12, marginTop: 14,
                                border: `1px solid ${h.gn || "#16a34a"}30`,
                            }}>
                                <div style={{ color: h.gn || "#16a34a", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                                    ✓ {t.done}
                                </div>
                                {m.asn_n && (
                                    <div style={{ color: h.tx, fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
                                        🛠️ {m.asn_n}
                                        {m.asn_p && <span style={{ color: h.sc, fontWeight: 400 }}> · {m.asn_p}</span>}
                                    </div>
                                )}
                                {m.exp_amt > 0 && (
                                    <div style={{ color: h.tx, fontSize: 12, marginBottom: 2 }}>
                                        {t.expenseInfo}: <span style={{ fontWeight: 700 }}>₹{m.exp_amt.toLocaleString()}</span> →{" "}
                                        {m.exp_target === "all"
                                            ? <span style={{ color: h.ac }}>{t.allTenants} ({t.commonSplit})</span>
                                            : <span style={{ color: h.wn }}>{tens.find(x => x.id === m.exp_target)?.name || "?"} ({t.chargedTo})</span>}
                                        {m.exp_applied && <span style={{ color: h.gn || "#16a34a", fontSize: 10 }}> ✓</span>}
                                    </div>
                                )}
                                {/* Has THIS tenant settled their share? The 🔧 payment
                                    row id is stamped pay_maint_<maintId>_<tenantId>_…
                                    so the ticket itself answers "did I pay for this?"
                                    without a trip to My Payments. */}
                                {m.exp_amt > 0 && (() => {
                                    const payRow = (pays || []).find(p => p.tid === ct && typeof p.id === "string" && p.id.startsWith(`pay_maint_${m.id}_`));
                                    if (!payRow) return null;
                                    const isPaid = payRow.st === "paid";
                                    const isPending = !isPaid && !!payRow.pending;
                                    const c = isPaid ? (h.gn || "#16a34a") : isPending ? (h.wn || "#d97706") : (h.rd || "#ef4444");
                                    const label = isPaid ? "✓ You paid this charge" : isPending ? "⏳ Your payment is awaiting landlord approval" : "Payment due — pay from My Payments";
                                    return <div style={{ display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, background: `${c}12`, color: c, fontSize: 11, fontWeight: 700, marginTop: 4, marginBottom: 2 }}>{label}</div>;
                                })()}
                                {m.sp_notes && (
                                    <div style={{ color: h.tx, fontSize: 12, marginTop: 6, paddingTop: 6, borderTop: `1px solid ${h.gn || "#16a34a"}30` }}>
                                        <span style={{ fontWeight: 600 }}>{t.workNotes || "Work Notes"}:</span> {m.sp_notes}
                                    </div>
                                )}
                                {m.asn_notes && (
                                    <div style={{ color: h.tx, fontSize: 12, marginTop: 6, paddingTop: 6, borderTop: `1px solid ${h.gn || "#16a34a"}30` }}>
                                        {m.asn_notes}
                                    </div>
                                )}
                                {/* Timeline — schema only stores created (m.dt) and
                                    confirmed_at; intermediate stage timestamps don't
                                    exist yet so we show what we have. */}
                                <div style={{
                                    marginTop: 10, paddingTop: 8,
                                    borderTop: `1px solid ${h.gn || "#16a34a"}30`,
                                    display: "flex", flexDirection: "column", gap: 4,
                                }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                                        <span style={{ color: h.sc }}>{t.created || "Created"}</span>
                                        <span style={{ color: h.tx, fontWeight: 500 }}>{m.dt || "—"}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                                        <span style={{ color: h.sc }}>{t.resolved || "Resolved"}</span>
                                        <span style={{ color: h.tx, fontWeight: 500 }}>{fmtTs(m.confirmed_at) || "—"}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action buttons. stopPropagation so taps
                            on Edit/Delete don't bubble up to the card's
                            collapse handler. */}
                        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                            {m.st !== "done" && (
                                <button onClick={(e) => { e.stopPropagation(); startEdit(m); }} style={{
                                    flex: 1, padding: 10, borderRadius: 10, border: "none",
                                    background: `${h.ac}12`, color: h.ac,
                                    cursor: "pointer", fontSize: 12, fontWeight: 600,
                                }}>{"✏️ " + t.edit}</button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); deleteMaint(m); }} style={{
                                flex: 1, padding: 10, borderRadius: 10, border: "none",
                                background: `${h.rd}12`, color: h.rd,
                                cursor: "pointer", fontSize: 12, fontWeight: 600,
                            }}>{"✕ " + t.delete}</button>
                        </div>
                    </div>
                </React.Fragment>
            );
        })}
    </div>;
}

export default TenMaintList;
